import { useForm } from 'react-hook-form';
import AddressAutocomplete from 'shared/components/forms/AddressAutocomplete';
import { usePlaceAddressField } from 'shared/hooks/usePlaceAddressField';
import { Field, INPUT, INPUT_ERR } from './form-utils';

export interface DeliveryData {
  deliveryAddress: string;
  deliveryLat?: number;
  deliveryLng?: number;
  recipientFirstName: string;
  recipientLastName: string;
  recipientPhone: string;
  recipientEmail: string;
}

interface Props {
  defaultValues?: Partial<DeliveryData>;
  onNext: (data: DeliveryData) => void;
  onBack: () => void;
}

export default function StepDelivery({ defaultValues, onNext, onBack }: Props) {
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<DeliveryData>({ defaultValues, mode: 'onSubmit', reValidateMode: 'onSubmit' });

  const deliveryField = usePlaceAddressField(register, setValue, getValues, trigger, {
    address: 'deliveryAddress',
    lat: 'deliveryLat',
    lng: 'deliveryLng',
  });
  const { ref: deliveryRef, onBlur: deliveryBlur, name: deliveryName } = deliveryField.registration;

  return (
    <form onSubmit={handleSubmit(onNext)}>
      <div className="fade-up bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-4">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <span
              className="material-symbols-outlined text-primary text-[20px]"
              style={{ fontVariationSettings: '"FILL" 1' }}
            >
              local_shipping
            </span>
          </div>
          <h3 className="font-black text-base text-slate-800">Delivery Details</h3>
        </div>

        <div className="flex flex-col gap-4">
          <Field label="Delivery address" error={errors.deliveryAddress?.message} hint="Start typing and choose from suggestions">
            <AddressAutocomplete
              key={`delivery-${defaultValues?.deliveryAddress ?? ''}-${defaultValues?.deliveryLat ?? ''}`}
              name={deliveryName}
              ref={deliveryRef}
              defaultValue={defaultValues?.deliveryAddress}
              onBlur={deliveryBlur}
              onChange={deliveryField.handleAddressChange}
              onPlaceSelect={deliveryField.handlePlaceSelect}
              onManualEdit={deliveryField.handleManualEdit}
              placeholder="456 Victoria Island, Lagos"
              className={`${INPUT} ${errors.deliveryAddress ? INPUT_ERR : ''}`}
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Recipient first name" error={errors.recipientFirstName?.message}>
              <input
                {...register('recipientFirstName', { required: 'Required' })}
                placeholder="Chioma"
                className={`${INPUT} ${errors.recipientFirstName ? INPUT_ERR : ''}`}
              />
            </Field>
            <Field label="Recipient last name" error={errors.recipientLastName?.message}>
              <input
                {...register('recipientLastName', { required: 'Required' })}
                placeholder="Obi"
                className={`${INPUT} ${errors.recipientLastName ? INPUT_ERR : ''}`}
              />
            </Field>
            <Field label="Recipient phone" error={errors.recipientPhone?.message}>
              <input
                {...register('recipientPhone', {
                  required: 'Required',
                  pattern: { value: /^[0-9+\s()-]+$/, message: 'Invalid phone number' },
                })}
                type="tel"
                placeholder="08098765432"
                className={`${INPUT} ${errors.recipientPhone ? INPUT_ERR : ''}`}
              />
            </Field>
            <Field label="Recipient email" error={errors.recipientEmail?.message}>
              <input
                {...register('recipientEmail', {
                  required: 'Required',
                  pattern: { value: /^\S+@\S+$/i, message: 'Invalid email' },
                })}
                type="email"
                placeholder="chioma@email.com"
                className={`${INPUT} ${errors.recipientEmail ? INPUT_ERR : ''}`}
              />
            </Field>
          </div>
        </div>
      </div>

      <div className="flex gap-3 mt-2">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-4 rounded-2xl border-2 border-slate-200 text-slate-600 font-extrabold text-sm hover:border-primary hover:text-primary transition-all"
        >
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          Back
        </button>
        <button
          type="submit"
          className="flex-1 bg-primary text-white font-extrabold px-8 py-4 rounded-2xl text-base hover:shadow-xl hover:shadow-primary/25 hover:scale-[1.02] transition-all active:scale-95 flex items-center justify-center gap-2 relative overflow-hidden group"
        >
          <span className="absolute inset-0 bg-primary-deep scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 rounded-2xl" />
          <span className="relative z-10">Continue to Package Details</span>
          <span className="material-symbols-outlined relative z-10 text-[20px]">arrow_forward</span>
        </button>
      </div>
    </form>
  );
}
